import { useMemo } from 'react';
import { useGetInvitedProjectsQuery } from '@frameio/graphql-client/generated';
import { DEFAULT_PROJECT_SORT_BY } from '@frameio/shared/resources/constants';
import type { ProjectSortBy } from '@frameio/shared/resources/projects/types';
import { appComponentsStorage } from '@frameio/shared/storage';
import { mapSortByToProjectOrderInputs } from '@frameio/shared/utils';
import { sortWorkspaceProjects } from '../../utils';
import { useWorkspacesProjectFilterAsInput } from './useWorkspacesProjectFilterAsInput';

export const useInvitedToProjects = (params: {
  accountId: string;
  sortBy?: ProjectSortBy;
}): {
  invitedToProjectIds: string[];
  invitedToProjectCount: number;
  isLoading: boolean;
} => {
  const projectStatusFilterInput = useWorkspacesProjectFilterAsInput();
  const projectOrder = mapSortByToProjectOrderInputs(
    appComponentsStorage.get(`frame-io--project-sort-by`) ||
      DEFAULT_PROJECT_SORT_BY
  );

  const { data, loading } = useGetInvitedProjectsQuery({
    variables: {
      accountId: params.accountId,
      filter: { projectStatus: projectStatusFilterInput },
      orders: [projectOrder],
    },
    skip: !params.accountId,
  });

  return useMemo(() => {
    if (!data)
      return {
        invitedToProjectIds: [],
        invitedToProjectCount: 0,
        isLoading: loading,
      };
    const fullAccessProjects = data?.account?.fullAccessProjects.nodes || [];
    const partialAccessProjects =
      data?.account?.partialAccessProjects.nodes || [];
    const invitedTo = sortWorkspaceProjects(
      [...fullAccessProjects, ...partialAccessProjects],
      params.sortBy ?? DEFAULT_PROJECT_SORT_BY
    );

    const activeProjectCount = data?.account?.fullAccessActiveProjectCount ?? 0;
    const inactiveProjectCount =
      data?.account?.fullAccessInactiveProjectCount ?? 0;

    return {
      isLoading: loading,
      invitedToProjectIds: invitedTo.map(({ id }) => id),
      invitedToProjectCount: activeProjectCount + inactiveProjectCount,
    };
  }, [data, params.sortBy, loading]);
};
